import { useState, useEffect } from 'react'
import { useAnnouncementStore, useAuthStore } from '../stores'
import { MessageSquare, Plus, Trash2, Edit2 } from 'lucide-react'

function AdminAnnouncements() {
  const { announcements, addAnnouncement, updateAnnouncement, deleteAnnouncement, fetchAnnouncements } = useAnnouncementStore()
  const { user } = useAuthStore()
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchAnnouncements()
  }, [fetchAnnouncements])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and content are required')
      setLoading(false)
      return
    }

    try {
      if (editingId) {
        await updateAnnouncement(editingId, formData)
        setEditingId(null)
      } else {
        await addAnnouncement({
          title: formData.title,
          content: formData.content,
          created_by: user?.id
        })
      }
      setFormData({ title: '', content: '' })
      setShowForm(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save announcement')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this announcement?')) return
    try {
      await deleteAnnouncement(id)
    } catch (error) {
      console.error('Failed to delete:', error)
    }
  }

  if (!user || (user.role !== 'admin' && user.role !== 'sub_admin')) {
    return (
      <div className="bg-red-900/30 border border-red-700 rounded-lg p-6 text-red-200">
        Access denied. Admin only.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-[#161b22] border border-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageSquare size={24} />
            Announcements
          </h2>
          <button
            onClick={() => {
              setShowForm(!showForm)
              setEditingId(null)
              setFormData({ title: '', content: '' })
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold transition"
          >
            <Plus size={18} />
            New Announcement
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-800 border border-gray-700 rounded-lg space-y-4">
            {error && <div className="p-3 bg-red-900/30 border border-red-700 rounded text-red-200 text-sm">{error}</div>}
            
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Announcement title"
                className="w-full px-4 py-2 bg-[#0d1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-green-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Content</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Announcement content"
                rows={5}
                className="w-full px-4 py-2 bg-[#0d1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-green-500 outline-none resize-none"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg text-white font-semibold transition"
              >
                {loading ? 'Saving...' : (editingId ? 'Update' : 'Send to All')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false)
                  setEditingId(null)
                  setFormData({ title: '', content: '' })
                }}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-semibold transition"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {announcements.length === 0 ? (
          <p className="text-gray-400">No announcements yet.</p>
        ) : (
          <div className="space-y-3">
            {announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="p-4 bg-gray-800 border border-gray-700 rounded-lg hover:border-blue-500/50 transition"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white">{announcement.title}</h3>
                    <p className="text-sm text-gray-400 mt-1">{new Date(announcement.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingId(announcement.id)
                        setFormData({ title: announcement.title, content: announcement.content })
                        setShowForm(true)
                      }}
                      className="p-2 bg-gray-700 hover:bg-yellow-900/20 hover:text-yellow-500 rounded text-gray-400 transition"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(announcement.id)}
                      className="p-2 bg-gray-700 hover:bg-red-900/20 hover:text-red-500 rounded text-gray-400 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className="text-gray-300 whitespace-pre-wrap">{announcement.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminAnnouncements
